import { Role, User } from '@/types';

// Hierarchy: super_user is highest (0), org_admin is middle (1), data_owner is lower (2)
export const ROLE_HIERARCHY: Role[] = ['super_user', 'org_admin', 'data_owner'];

/** The single source of role display labels — the lower_snake wire values are not presentable.
 *  Index it directly for a typed `Role`; for an open `string`, go through `humanizeRole`
 *  (`@/helpers/roles`), which title-cases values missing from this map instead of leaking them. */
export const ROLE_LABELS: Record<Role, string> = {
  super_user: 'Super Admin',
  org_admin: 'Org Admin',
  data_owner: 'Data Owner',
};

/** Lower number = higher permission. */
const getRoleLevel = (role: Role): number => ROLE_HIERARCHY.indexOf(role);

export const hasRole = (currentRole: Role | undefined, requiredRole: Role): boolean => currentRole === requiredRole;

export const hasAnyRole = (currentRole: Role | undefined, requiredRoles: Role[]): boolean =>
  !!currentRole && requiredRoles.includes(currentRole);

/** Index 0 is the highest permission, hence `<=` rather than `>=`. */
export const hasMinimumRole = (currentRole: Role | undefined, requiredRole: Role): boolean => {
  if (!currentRole) return false;
  return getRoleLevel(currentRole) <= getRoleLevel(requiredRole);
};

export const isSuperAdmin = (currentRole: Role | undefined): boolean => currentRole === 'super_user';

export const isOrgAdmin = (currentRole: Role | undefined): boolean => currentRole === 'org_admin';

export const isDataOwner = (currentRole: Role | undefined): boolean => currentRole === 'data_owner';

/** Null for a missing role, so a caller can distinguish "no role" from a real label. */
export const getRoleLabel = (role: Role | undefined): string | null => (role ? (ROLE_LABELS[role] ?? role) : null);

/** Platform-wide, independent of any org-specific role. */
export const isGlobalSuperAdmin = (user: User | null | undefined): boolean => user?.is_super_admin ?? false;

export const getAllRoles = (): ReadonlyArray<Role> => ROLE_HIERARCHY;
